import React, {Component} from 'react';
import {get} from '../../util/facebook.settings';
import {ButtonIcon, ButtonPrimary} from '@homeaway/react-buttons';
import {SVGIcon} from '@homeaway/react-svg';
import {FACEBOOK__24} from '@homeaway/svg-defs';
import Script from 'react-load-script';
import PropTypes from 'prop-types';
import ClientLogger from '../../util/clientLogger';
import authAccountTypes from '../../enums/AuthAccountTypes';
import umsSettings from '../../util/ums.settings';
import './FacebookAuthComponent.less';
import {TrackingContext} from '@homeaway/react-event-tracking';
import aamClient from '../../util/aamClient';

class FacebookAuthComponent extends Component {
    static get propTypes() {
        return {
            enableLoading: PropTypes.func,
            disableLoading: PropTypes.func
        };
    }

    static get defaultProps() {
        return {
            enableLoading: () => {},
            disableLoading: () => {}
        };
    }

    constructor(props) {
        super(props);
        // Window doesn't exist server side so this is needed, we need to pass a valid host name or component will not load in catalyst app!
        let hostName = (typeof window !== 'undefined') ? window.location.hostname : 'localhost.com';
        const hostSettings = get(hostName, this.props.locale);
        this.scriptUrl = `https://connect.facebook.net/${hostSettings.locale}/sdk.js`;
        // these are all the fields facebook gives you for free
        // being explicit as light box never makes this call
        this.scope = 'public_profile,email';
        this.state = {
            loggedIn: false
        };
        // per https://developers.facebook.com/docs/javascript/quickstart
        if (typeof window !== 'undefined') { // Window doesn't exist server side so this is needed or app will fail
            window.fbAsyncInit = function() {
                try {
                    window.FB.init({
                        appId: hostSettings.appId,
                        autoLogAppEvents: true,
                        xfbml: false,
                        version: 'v2.10',
                        oauth: true,
                        status: true
                    });
                } catch (error) {
                    ClientLogger.logError(`An error has occurred while trying to initialize FB script: ${error}`);
                }
            };
        }
    }

    componentDidMount() {
        if (this.props.token) {
            this.signUpAttempt();
            this.register(this.props.token, this.props.emailAddress);
        }
    }

    loginSuccess = (json) => {
        this.context.trigger('login.success', {logintype: 'facebook', loginsource: 'lex', publicuuid: json.publicUuid});
        this.authSuccess(json);
    }

    loginFailure = () => {
        this.setState({loggedIn: false});
        this.context.trigger('login.failure', {logintype: 'facebook', loginsource: 'lex'});
    }

    loginAttempt = () => {
        this.context.trigger('login.attempt', {logintype: 'facebook', loginsource: 'lex'});
    }

    displayErrorMessage = (error, errorTitle) => {
        this.props.handleError(error, errorTitle);
    }

    authSuccess = (json) => {
        this.setState({loggedIn: true});
        this.props.setAuthJson(json);
        this.props.onClose();
    }

    signUpSuccess = (json) => {
        if (this.props.marketingOptOut !== null) {
            this.context.trigger('user.account.created', {
                logintype: 'facebook',
                loginsource: 'lex',
                newusertype: 'traveler',
                marketingoptout: this.props.marketingOptOut,
                publicuuid: json.publicUuid
            });
        } else {
            this.context.trigger('user.account.created', {
                logintype: 'facebook',
                loginsource: 'lex',
                newusertype: 'traveler',
                publicuuid: json.publicUuid
            });
        }
        // set logged in state and close modal
        this.authSuccess(json);
    }

    signUpFailure = (statusCode) => {
        this.setState({loggedIn: false});
        if (this.props.marketingOptOut !== null) {
            this.context.trigger('user.account.creation.failure', {
                logintype: 'facebook',
                loginsource: 'lex',
                newusertype: 'traveler',
                failurereason: `${statusCode}`,
                marketingoptout: this.props.marketingOptOut
            });
        } else {
            this.context.trigger('user.account.creation.failure', {
                logintype: 'facebook',
                loginsource: 'lex',
                newusertype: 'traveler',
                failurereason: `${statusCode}`
            });
        }
    }

    signUpAttempt() {
        if (this.props.marketingOptOut !== null) {
            this.context.trigger('user.account.creation.attempt', {
                logintype: 'facebook',
                loginsource: 'lex',
                marketingoptout: this.props.marketingOptOut,
                newusertype: 'traveler'
            });
        } else {
            this.context.trigger('user.account.creation.attempt', {
                logintype: 'facebook',
                loginsource: 'lex',
                newusertype: 'traveler'
            });
        }
    }

    facebookGenericEdap = (message) => {
        this.context.trigger('generic.event', {eventaction: `lexFacebookAuth.${message}`, eventcategory: 'lexFacebookAuth'});
    }

    handleScriptError = () => {
        this.context.trigger('generic.event', {
            eventaction: 'lexFacebookAuth.facebookScriptError',
            eventcategory: 'lexFacebookAuth'
        });
    }

    handleScriptLoad = () => {
        this.context.trigger('generic.event', {
            eventaction: 'lexFacebookAuth.facebookScriptLoad',
            eventcategory: 'lexFacebookAuth'
        });
    }

    handleJson(json, email, token) {
        if (json) {
            if (authAccountTypes.isNotUndefined(json.authType)) {
                let SIGNUP = this.checkAuthType(json.authType, authAccountTypes.SIGNUP);
                if (SIGNUP && umsSettings.isTCRequired(this.props.site)) {
                    // this is the SignUp scenario, so we need to update the pageName to TermsAndConditions only for the required EU brands
                    let pageName = 'TermsAndConditions';
                    this.props.performBulkPageUpdate(pageName, email, authAccountTypes.FACEBOOK, token);
                    this.props.disableLoading();
                } else {
                    let umsOnly = this.checkAuthType(json.authType, authAccountTypes.UMS);
                    if (SIGNUP || umsOnly) {
                        this.signUpAttempt();
                        this.register(token, email);
                    } else {
                        this.loginAttempt();
                        this.authenticate(token, email);
                    }
                }
            }
        }
    }

    checkAuthType(accountType, loginType) {
        return accountType.some((t) => (t === loginType));
    }

    facebookAuthStatus(token, email) {
        const payload = {
            accessToken: token,
            authType: 'FACEBOOK'
        };

        return new Promise((resolve, reject) => {
            aamClient.tokenStatus(payload).then((response) => {
                if (response.status >= 200 && response.status < 400) {
                    response.json().then((json) => {
                        this.handleJson(json, email, token);
                        return resolve();
                    });
                }
            }).catch((e) => {
                // If user denied or something else failed we log it, except if they just close pop up, no error should be displayed then
                if (typeof e === 'undefined' || e.error !== 'popup_closed_by_user') {
                    this.displayErrorMessage(this.props.lingua.read('error.generic'), '');
                }
                return reject(e);
            });
        });
    }

    authenticate(token, email) {
        const payload = {
            'userName': email,
            'authType': 'FACEBOOK',
            'secret': token,
            'rememberMe': true
        };

        return new Promise((resolve, reject) => {
            aamClient.authenticate(payload).then((response) => {
                this.checkStatus(response, resolve, reject, false);
            }).catch((e) => {
                this.failLogin(e, reject, false);
            });
        });
    }

    register(token, email) {
        const payload = {
            userName: email,
            secret: token,
            authType: 'FACEBOOK',
            rememberMe: true
        };
        return new Promise((resolve, reject) => {
            aamClient.register(payload).then((response) => {
                this.checkStatus(response, resolve, reject, true);
            }).catch((e) => {
                return this.failLogin(e, reject, true);
            });
        });
    }

    failLogin(e, reject, isRegister) {
        this.props.disableLoading();
        if (isRegister === true) {
            this.signUpFailure(e);
        } else {
            this.loginFailure();
        }
        this.displayErrorMessage(this.props.lingua.read('error.generic'), '');
        return reject(e);
    }

    checkStatus(response, resolve, reject, isRegister) {
        if (response.status >= 200 && response.status < 400) {
            response.json().then((json) => {
                if (isRegister === true) {
                    this.signUpSuccess(json);
                } else {
                    this.loginSuccess(json);
                }
            });
            return resolve();
        }
        if (isRegister === true) {
            this.signUpFailure(response.status);
        } else {
            this.loginFailure();
        }
        this.props.disableLoading();
        this.displayErrorMessage(this.props.lingua.read('error.generic'), '');
        return reject();
    }

    authFacebook = (response) => {
        if (response.authResponse) {
            let component = this;
            /* istanbul ignore next */
            window.FB.api('/me', {fields: 'email'}, (me) => {
                let token = response.authResponse.accessToken;
                let email = me.email;
                component.props.enableLoading();
                component.facebookAuthStatus(token, email);
            });
        } else {
            this.facebookGenericEdap('facebookScriptResponseError');
        }
    }

    displayFacebookLabel() {
        if (this.props.expediaIconDisplayed === true) {
            return (
                <div className="Facebook_label">
                    <label>{this.props.lingua.read('lex.facebook.icon.label')}</label>
                </div>
            );
        }
        return (
            <div/>
        );
    }

    displayAppropriateButton() {
        if (typeof this.props.authStatus !== 'undefined') {
            return (
                <div className="SocialButtons__icon">
                    <ButtonPrimary label="" block className="btn--facebook btn-block" onClick={this.onClickFB} name={this.props.name} id={this.props.id}>
                        <SVGIcon className="btn--spacing" inlineFlex markup={FACEBOOK__24} />
                        <span>{this.props.lingua.read('lex.social.login.with.card.message.facebook')}</span>
                    </ButtonPrimary>
                </div>
            );
        }
        return (
            <div className="small">
                <ButtonIcon className="Facebook__icon" circle svgMarkup={FACEBOOK__24} label="Facebook" onClick={this.onClickFB} name={this.props.name} id={this.props.id}/>
                {this.displayFacebookLabel()}
            </div>
        );
    }

    onClickFB = () => {
        this.facebookGenericEdap('facebookButtonClicked');
        try {
            window.FB.login(this.authFacebook, {scope: this.scope, auth_type: 'rerequest'});
        } catch (error) {
            ClientLogger.logError(`An error has occurred while trying to login via FB: ${error}`);
            this.facebookGenericEdap(error);
            this.displayErrorMessage(this.props.lingua.read('lex.social.browser.error'), this.props.lingua.read('lex.social.browser.error.title'));
        }
    }

    render() {
        return (
            <div>
                <Script
                    url={this.scriptUrl}
                    onError={this.handleScriptError}
                    onLoad={this.handleScriptLoad}
                />
                {this.displayAppropriateButton()}
            </div>
        );
    }
}

FacebookAuthComponent.contextType = TrackingContext;
export default FacebookAuthComponent;
